/**
 * ⭐⭐⭐ `R2-UI8` · **El panel lateral de una respuesta automática — el MISMO
 * para crear y para editar.** `// ADR-0348`
 * `R2-F3-CA-14`…`CA-21`, `CA-23`…`CA-26`, `CA-29`…`CA-32`, `PRD_R2.md:1100-1131`.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⭐⭐ ① UN SOLO COMPONENTE, Y LAS DOS APERTURAS **NO** SON SIMÉTRICAS
 * ════════════════════════════════════════════════════════════════════════════
 *
 * `CA-14`/`CA-15` piden que «Nueva respuesta automática» **tenga dirección
 * propia** (`…/botones/nueva`) y que el panel **siga abierto después de
 * recargar**; `CA-29` pide que «Editar» abra **el mismo panel** y que la
 * dirección **siga en `…/botones`**.
 *
 * ⇒ las dos aperturas usan este componente y **entran por caminos distintos**:
 *
 *   · **crear** ... lo monta LA DIRECCIÓN (`panelNuevoBoton` desde una `<Route>`
 *     hermana). No hay estado de apertura y no puede haberlo: por eso sobrevive
 *     a la recarga.
 *   · **editar** ... lo monta un `useState` de la pantalla. **Y eso es lo
 *     correcto acá**, al revés que en «Agregar plantilla»: `CA-29` exige que la
 *     dirección NO cambie, así que darle una dirección propia
 *     (`…/botones/<id>/editar`) sería lo simétrico y está **prohibido**.
 *
 * ⛔⛔ **La simetría es la trampa, y tiene un control negativo propio:** recargar
 * estando en «Editar» tiene que volver a la lista **sin el panel**. Un `useState`
 * lo cumple; una dirección propia lo rompe, y las dos se ven igual mientras nadie
 * recargue.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⛔⛔⛔ ② `25` PUNTOS DE CÓDIGO ≠ `25` UNIDADES UTF-16, Y `maxLength` CUENTA LAS
 *      SEGUNDAS
 * ════════════════════════════════════════════════════════════════════════════
 *
 * `CA-16`: *«no admite más de 25 caracteres»*. El atributo HTML `maxlength`
 * —que es lo que cualquiera escribe— **cuenta unidades UTF-16**, así que un texto
 * de 24 letras más un emoji mide **25 puntos de código** (válido para el
 * servidor, `BUTTON_TEXT_MAX_CODEPOINTS`) y **26 unidades UTF-16**: el atributo
 * le comería el último carácter al operador, y el equipo del estudio escribe
 * botones con emojis.
 *
 * ⇒ **no hay `maxLength` en este archivo.** El recorte lo hace
 * `contarPuntosDeCodigo` en el `onChange`, con la misma forma que
 * `countCodePoints` del contrato.
 *
 * ⚠️ **El único caso que discrimina las dos cuentas es el del emoji.** Un examen
 * escrito con texto ASCII pasa igual con la implementación equivocada; por eso el
 * caso del emoji está escrito y corrido con su mutante.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⛔⛔ ③ LAS DOS VALIDACIONES DE VACÍO **NO** SE TRATAN IGUAL, Y LOS CA LO PIDEN
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Se lee como una inconsistencia y **es el criterio, leído dos veces**:
 *
 *   · `CA-23` ... *««Guardar respuesta» está deshabilitado, y sigue deshabilitado
 *     con tres espacios en el texto del botón»* ⇒ el botón mira `trim()` del
 *     TEXTO DEL BOTÓN.
 *   · `CA-25` ... *«con tres espacios en la respuesta, «Guardar respuesta»
 *     MUESTRA «Escribe qué responde KAI. Es obligatorio.»»* ⇒ con espacios el
 *     botón **tiene que estar habilitado**, porque si no no se puede tocar y el
 *     mensaje no aparece nunca.
 *   · `CA-24` ... *«con la respuesta VACÍA, «Guardar respuesta» está
 *     deshabilitado»* ⇒ vacío sí deshabilita.
 *
 * ⇒ **texto del botón: `trim()`. Respuesta: crudo para habilitar, `trim()` al
 * tocar.** Unificar los dos con `trim()` deja `CA-25` en rojo y unificarlos con
 * el crudo deja `CA-23` en rojo. No hay una tercera lectura que cumpla las dos.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⛔⛔ ④ «ADEMÁS DE RESPONDER»: 4 Ó 3 POR CAPACIDAD, Y `null` NO ES `false`
 * ════════════════════════════════════════════════════════════════════════════
 *
 * `CA-19` (cuatro en el Híbrido) y `CA-21` (el Puro no ofrece «pidió humano», ni
 * al crear **ni al editar**). `ADR-0362` metió la capacidad por CONFIG:
 * `derivacionHabilitadaDe(config, brazo)` devuelve **`boolean | null`**.
 *
 * ⛔ **Nunca se compara el brazo contra un literal de brazo.**
 * `gates/literales-de-brazo.spec.ts` lo prohíbe, y un `brazo === "puro"` ata esta
 * pantalla a una instancia. `padron/tabla.tsx:245` todavía lo hace —es de
 * `R2-UI9`, no de acá—.
 *
 * ⛔⛔ **Y `null` NO se colapsa con `false`, que es la salida cómoda y la que
 * `configuracion.ts` §`derivacionHabilitadaDe` declara como defecto:**
 *
 *   · `true` ......... cuatro opciones.
 *   · `false` ........ tres. El brazo **declaró** que no deriva.
 *   · `null` ......... tres, **y se dice por qué**: la instancia no lo declaró.
 *
 * ⚠️ Las dos últimas dibujan las mismas tres opciones **y eso es correcto**: lo
 * que no puede pasar es que el operador no pueda distinguir «este brazo no
 * deriva» de «nadie configuró esto». Un `?? false` compila, se ve idéntico, y
 * borra esa distinción para siempre. El caso del `null` tiene examen propio, que
 * es lo que `configuracion.ts` declara que es *«de `R2-UI8`»*.
 *
 * ⚠️ **Ofrecer las cuatro siempre «total el servidor rechaza» está descartado:**
 * produce el `400 notPermittedInArm` que `CA-21` dice que **no puede ni
 * intentarse** (regla 15: *«No existe, ni en pantalla ni por pedido al
 * servidor»*).
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⛔ ⑤ EL `409` LO REDACTA LA PANTALLA, NO EL SERVIDOR — `ADR-0201`
 * ════════════════════════════════════════════════════════════════════════════
 *
 * `CA-17`/`CA-31` piden *«Ya existe una respuesta automática para «[botón]» en
 * KAI · Brazo X. Edita esa.»* — con **el texto del botón adentro**. `ADR-0201`
 * prohíbe que el cuerpo de error del servidor traiga valores del pedido, así que
 * el `409` no puede traer esa frase.
 *
 * ⇒ el borde dice **cuál desenlace fue** (`esRepetida`) y la frase la arma acá,
 * con el texto que se MANDÓ y el sello del brazo. Mostrar el texto crudo del
 * cuerpo es lo cómodo y depende de que el servidor viole `ADR-0201`.
 *
 * ⚠️ **Y se arma con lo que se mandó, no con lo que hay en el campo AHORA**: si
 * el operador sigue tecleando mientras el `409` viaja, la frase nombraría un
 * texto que nunca se mandó.
 */

import { useEffect, useState } from "react"
import type { JSX } from "react"
import type { AutoReplyAction } from "@contracts/admin-api/index.ts"
import {
  ACCION_DE_BAJA,
  ACCION_DE_DERIVACION,
  ACCION_POR_DEFECTO,
  ACCIONES_EN_ORDEN,
  contarPuntosDeCodigo,
  MAX_RESPUESTA,
  MAX_TEXTO_DE_BOTON,
} from "./borde-botones.ts"
import type { DatosDelPanel, FilaDeRespuesta } from "./borde-botones.ts"

/**
 * ⭐ **Los rótulos y las ayudas de las cuatro opciones, palabra por palabra**
 * (`PRD_R2.md:1114-1119`, `CA-19` y `CA-20`).
 *
 * ⛔ **Es un `Record<AutoReplyAction, …>` con el tipo ANOTADO**, y ésa es la
 * única forma que mide algo: el día que `contracts/channel` agregue una quinta
 * acción, **este objeto deja de compilar** hasta que alguien escriba su rótulo y
 * su ayuda. Un `Record<string, …>` compila con cuatro de cinco y la opción nueva
 * simplemente no se ofrecería, sin que nada lo dijera. Es la misma disciplina que
 * `AUTO_REPLIES_OUTCOME_STATUS` aplica del lado del cable.
 */
const OPCIONES: Readonly<Record<AutoReplyAction, { readonly rotulo: string; readonly ayuda: string }>> =
  {
    ninguna: {
      rotulo: "Sólo responder",
      ayuda: 'Cambia el estado del participante a "Sin interacción: No". Ningún otro estado cambia.',
    },
    siguio_con_chatbot: {
      rotulo: "Marcar que siguió con el chatbot",
      ayuda: 'Además, cambia el estado del participante a "Siguió con el chatbot: Sí".',
    },
    pidio_humano: {
      rotulo: "Marcar que pidió hablar con una persona",
      ayuda: 'Además, cambia el estado del participante a "Pidió humano: Sí".',
    },
    opt_out: {
      rotulo: "Dar de baja al participante",
      ayuda:
        'Además, cambia el estado del participante a "Opt-out: Sí": deja de recibir envíos y, si escribe, recibe el mensaje de servicio no disponible.',
    },
  }

// ⟦rastro · ADR-0082 · R4-UIT1 · 4/10/2026⟧ Los textos viejos de estas constantes («… KAI no
// va a reconocerlo.», «Escribe qué responde KAI.», «… y KAI va a seguir escribiéndole…» y el paso
// «2 · Qué responde KAI», PRD_R4.md:1982-1985) se conservan acá en comentario; el texto visible
// pasó a «el chatbot».

/** `CA-16` · la ayuda del paso 1, palabra por palabra (`PRD_R2.md:1106`). */
export const AYUDA_DEL_BOTON =
  "Escríbelo tal cual está en Meta. Si no coincide, el chatbot no va a reconocerlo."

/** `CA-18` · la ayuda del paso 2, palabra por palabra (`PRD_R2.md:1110`). */
export const AYUDA_DE_LA_RESPUESTA =
  "Es obligatorio: no se puede guardar un botón sin respuesta."

/** `PRD_R2.md:1127` · el vacío del texto del botón. Lo decide la PANTALLA, no el servidor. */
export const ERROR_TEXTO_VACIO = "Escribe el texto del botón, tal cual está en Meta."

/** `CA-25` / `PRD_R2.md:1128` · el vacío de la respuesta. Lo decide la PANTALLA. */
export const ERROR_RESPUESTA_VACIA = "Escribe qué responde el chatbot. Es obligatorio."

/**
 * `CA-32` · **el aviso PERMANENTE del botón de baja al editarlo**
 * (`PRD_R2.md:1141`). Va arriba y no se puede cerrar: es el único control que
 * tiene el operador para no romper la baja con un typo.
 */
export const AVISO_DEL_BOTON_DE_BAJA =
  "Este es el botón con el que los participantes se dan de baja. Si su texto deja de coincidir con el de Meta, la baja deja de funcionar y el chatbot va a seguir escribiéndole a quien pidió salir."

/**
 * ⭐ `CA-17` / `CA-31` · la frase del `409`, armada ACÁ. Ver el bloque ⑤.
 *
 * ⛔ Recibe el texto que se MANDÓ, no el que está en el campo: son dos valores
 * distintos en cuanto el operador siga tecleando.
 */
export function mensajeDeRepetida(textoMandado: string, sello: string): string {
  return `Ya existe una respuesta automática para «${textoMandado}» en ${sello}. Edita esa.`
}

/**
 * ⭐⭐ **Qué opciones ofrece el brazo.** Ver el bloque ④: `true` ⇒ las cuatro;
 * `false` y `null` ⇒ las tres, sin la de derivación.
 *
 * ⛔ **Se filtra por la ACCIÓN y no por el índice ni por el brazo**: sacar «la
 * tercera» dependería de que nadie reordene `ACCIONES_EN_ORDEN`, y comparar el
 * brazo contra un literal está prohibido.
 */
export function opcionesDelBrazo(
  derivacionHabilitada: boolean | null,
): readonly AutoReplyAction[] {
  return derivacionHabilitada === true
    ? ACCIONES_EN_ORDEN
    : ACCIONES_EN_ORDEN.filter((accion) => accion !== ACCION_DE_DERIVACION)
}

export function PanelDeRespuesta({
  sello,
  fila,
  derivacionHabilitada,
  enCurso,
  error,
  onCancelar,
  onGuardar,
}: {
  /** El nombre del brazo, ya armado («KAI · Brazo Híbrido»). */
  readonly sello: string
  /**
   * La fila que se edita, o `null` para crear.
   *
   * ⛔ Es lo ÚNICO que distingue los dos modos: no hay una prop `modo` que pueda
   * quedar en desacuerdo con los datos. Un `modo: "editar"` con `fila: null` es
   * expresable y este tipo no lo deja escribir.
   */
  readonly fila: FilaDeRespuesta | null
  /** `boolean | null` de `derivacionHabilitadaDe`. ⛔ `null` NO es `false`, ver ④. */
  readonly derivacionHabilitada: boolean | null
  readonly enCurso: boolean
  /** El rechazo del servidor, ya redactado por la pantalla. El panel NO se cierra. */
  readonly error: string | null
  readonly onCancelar: () => void
  readonly onGuardar: (datos: DatosDelPanel) => void
}): JSX.Element {
  const editando = fila !== null

  const [texto, setTexto] = useState(fila?.buttonText ?? "")
  const [respuesta, setRespuesta] = useState(fila?.replyText ?? "")
  const [accion, setAccion] = useState<AutoReplyAction>(fila?.action ?? ACCION_POR_DEFECTO)
  /** El error que decide ESTA pantalla (los dos de vacío). El del servidor llega por prop. */
  const [errorPropio, setErrorPropio] = useState<string | null>(null)

  /**
   * ⭐ `CA-27` / `CA-38` · **Escape = «Cancelar», y llama al MISMO manejador.**
   *
   * ⛔ Va en `document` y no en el `<aside>`: con el panel abierto por URL
   * (`…/botones/nueva`) el foco puede no estar en ningún control de adentro.
   *
   * ⛔ Y las dos salidas —el botón y la tecla— llaman a `onCancelar`. Dos cuerpos
   * distintos serían dos caminos que pueden divergir, y el que divergiría en
   * silencio es el de teclado, que nadie mira.
   */
  useEffect(() => {
    const alPresionar = (evento: KeyboardEvent): void => {
      if (evento.key === "Escape") onCancelar()
    }
    document.addEventListener("keydown", alPresionar)
    return () => document.removeEventListener("keydown", alPresionar)
  }, [onCancelar])

  const puntosDelTexto = contarPuntosDeCodigo(texto)
  const puntosDeLaRespuesta = contarPuntosDeCodigo(respuesta)

  /*
   * ⛔ Ver el bloque ③: el texto del botón se mira con `trim()` (`CA-23`) y la
   * respuesta con el valor CRUDO (`CA-24` vs. `CA-25`). No es un descuido.
   */
  const noSePuedeGuardar =
    texto.trim() === "" ||
    puntosDelTexto > MAX_TEXTO_DE_BOTON ||
    respuesta === "" ||
    puntosDeLaRespuesta > MAX_RESPUESTA ||
    enCurso

  const ofrecidas = opcionesDelBrazo(derivacionHabilitada)

  function intentarGuardar(): void {
    /*
     * ⛔ Los DOS de vacío los decide la pantalla (guard 4 de la etapa): el
     * servidor los rechazaría con un `400 malformed` que no dice cuál de los
     * cuatro fue, y `CA-25` pide el texto exacto.
     */
    if (texto.trim() === "") {
      setErrorPropio(ERROR_TEXTO_VACIO)
      return
    }
    if (respuesta.trim() === "") {
      setErrorPropio(ERROR_RESPUESTA_VACIA)
      return
    }
    setErrorPropio(null)
    /*
     * ⚠️ **Se manda `texto` tal cual, sin `trim()` y sin normalizar.**
     * `DatosDeRespuestaAutomatica` dice *«`buttonText` va SIN normalizar: lo que
     * el operador escribió»*; la clave única la deriva `modules/channel`.
     * Recortarlo acá sería el navegador eligiendo la clave.
     */
    onGuardar({ buttonText: texto, replyText: respuesta, action: accion })
  }

  return (
    /*
     * ⚠️ `role="dialog"` con `aria-modal="false"` y **no `Dialogo`**: `CA-15` pide
     * que el panel **deje ver la lista detrás**, y `Dialogo` pinta un overlay
     * opaco y declara `aria-modal="true"`, que le dice al lector de pantalla que
     * lo de atrás no existe. Es el mismo tratamiento que `PanelAgregar`, y el
     * popup de eliminar sí usa `Dialogo` porque ahí sí hay que bloquear.
     *
     * ⚠️ La clase es `configuracion-chatbot__panel-lateral` —la primitiva de
     * «Nueva categoría» que `PRD_R2.md:1100`/`:2474` nombra— y **no una clase
     * nueva**: `estilos.css` no es territorio de esta etapa y una clase sin regla
     * renderiza sin estilo poniendo rojo a `pruebas/r11-clases-sin-regla.test.ts`.
     */
    <aside
      className="configuracion-chatbot__panel-lateral"
      role="dialog"
      aria-label={editando ? "Editar respuesta automática" : "Nueva respuesta automática"}
      aria-modal="false"
    >
      <header className="configuracion-chatbot__panel-cabecera">
        <h2>{editando ? "Editar respuesta automática" : "Nueva respuesta automática"}</h2>
      </header>

      {/*
        ⭐ `CA-32` · el aviso del botón de baja, PERMANENTE y ARRIBA. Sólo cuando
        se está editando una fila cuya acción es la de baja: al crear todavía no
        hay ninguna baja que romper.

        ⛔ Se decide por `fila.action` —la acción GUARDADA— y no por `accion` —la
        que está elegida en el formulario—: si el operador está sacándole la baja
        a ese botón, el aviso tiene que seguir mientras el cambio no se guarde.
      */}
      {editando && fila.action === ACCION_DE_BAJA && (
        <p className="configuracion-chatbot__alerta" role="note">
          {AVISO_DEL_BOTON_DE_BAJA}
        </p>
      )}

      {/* ── PASO 1 · EL BOTÓN ────────────────────────────────────────────── */}
      <div className="configuracion-chatbot__campo">
        <label className="configuracion-chatbot__etiqueta" htmlFor="boton-texto">
          1 · El botón
        </label>
        {/*
          ⛔⛔ **SIN `maxLength`.** Ver el bloque ②: ese atributo cuenta unidades
          UTF-16 y el límite es de PUNTOS DE CÓDIGO. El recorte va en el
          `onChange`, que es el único lugar donde se puede contar bien.

          ⛔ **Y es un `<input type="text">` libre, no un `<select>` del
          catálogo.** `PRD_R2.md:48`/`:275` y `AW9 ④` preveían el selector; `A10-1`
          lo deroga y `CA-16`/`CA-30` piden «campo de texto escribible». Un
          selector además haría imposible `CA-60` («sí guarda un botón que no está
          en ninguna plantilla»), que es el caso que la columna «Dónde aparece»
          existe para destapar.
        */}
        <input
          id="boton-texto"
          type="text"
          value={texto}
          autoComplete="off"
          onChange={(evento) => {
            const escrito = evento.target.value
            setTexto(
              contarPuntosDeCodigo(escrito) <= MAX_TEXTO_DE_BOTON
                ? escrito
                : [...escrito].slice(0, MAX_TEXTO_DE_BOTON).join(""),
            )
            if (errorPropio !== null) setErrorPropio(null)
          }}
        />
        <p className="configuracion-chatbot__ayuda">{AYUDA_DEL_BOTON}</p>
      </div>

      {/* ── PASO 2 · QUÉ RESPONDE EL CHATBOT ─────────────────────────────── */}
      <div className="configuracion-chatbot__campo">
        <label className="configuracion-chatbot__etiqueta" htmlFor="boton-respuesta">
          2 · Qué responde el chatbot
        </label>
        {/*
          ⛔ **La respuesta NO se recorta**, al revés que el texto del botón:
          `CA-26` pide que con 4.097 caracteres el botón quede DESHABILITADO, y
          para eso el operador tiene que poder llegar a escribir 4.097. Recortar
          acá dejaría ese criterio inalcanzable —el campo nunca pasaría de 4.096—
          y el caso daría verde sin haber medido nada.
        */}
        <textarea
          id="boton-respuesta"
          value={respuesta}
          onChange={(evento) => {
            setRespuesta(evento.target.value)
            if (errorPropio !== null) setErrorPropio(null)
          }}
        />
        <p className="configuracion-chatbot__ayuda">{AYUDA_DE_LA_RESPUESTA}</p>
        {/*
          ⭐ `CA-18` · el contador «N / 4096», **en PUNTOS DE CÓDIGO**. Con
          «¡Me alegra mucho!» dice «17 / 4096».
        */}
        <p
          className={
            puntosDeLaRespuesta > MAX_RESPUESTA
              ? "configuracion-chatbot__contador configuracion-chatbot__contador--pasado"
              : "configuracion-chatbot__contador"
          }
        >
          {puntosDeLaRespuesta} / {MAX_RESPUESTA}
        </p>
      </div>

      {/* ── PASO 3 · ADEMÁS DE RESPONDER ─────────────────────────────────── */}
      <fieldset className="configuracion-chatbot__campo">
        <legend className="configuracion-chatbot__etiqueta">3 · Además de responder</legend>
        <ul className="configuracion-chatbot__opciones">
          {ofrecidas.map((una) => (
            <li
              key={una}
              className={
                accion === una
                  ? "configuracion-chatbot__opcion configuracion-chatbot__opcion--activa"
                  : "configuracion-chatbot__opcion"
              }
            >
              <label>
                <input
                  type="radio"
                  name="ademas-de-responder"
                  value={una}
                  checked={accion === una}
                  onChange={() => setAccion(una)}
                />
                <span>{OPCIONES[una].rotulo}</span>
              </label>
              {/*
                ⭐ `CA-20` · cada opción NOMBRA el estado que cambia. Sin la ayuda,
                «Marcar que siguió con el chatbot» no dice qué queda registrado, y
                el operador no puede saber cuál de las cuatro le toca.
              */}
              <p className="configuracion-chatbot__ayuda">{OPCIONES[una].ayuda}</p>
            </li>
          ))}
        </ul>
        {/*
          ⭐⭐ Ver el bloque ④: **`null` NO es `false`, y acá es donde se ve.**
          Las tres opciones son las mismas en los dos casos; lo que cambia es que
          este renglón existe sólo cuando la instancia **no declaró nada**. Sin
          él, «el brazo declaró que no deriva» y «nadie configuró esto» serían el
          mismo dibujo, que es lo que `configuracion.ts` §`derivacionHabilitadaDe`
          pide que la pantalla que lo consume NO haga.
        */}
        {derivacionHabilitada === null && (
          <p className="configuracion-chatbot__nota" role="note">
            Esta instancia no declara si {sello} puede derivar a una persona, así que esa opción no
            se ofrece. No es lo mismo que haber declarado que no deriva.
          </p>
        )}
      </fieldset>

      {/*
        ⛔ El error va DEBAJO, en rojo, **y el panel NO se cierra**
        (`PRD_R2.md:1126`). Cerrarlo mandaría al operador a una lista donde su
        respuesta no está, sin nada que explique por qué.

        ⚠️ Son DOS fuentes y una sola caja: el de la pantalla (los vacíos) y el
        del servidor (el `409`). El propio gana porque es el más reciente — si el
        operador corrigió el texto y lo dejó vacío, el `409` viejo ya no describe
        lo que pasa.
      */}
      {(errorPropio ?? error) !== null && (
        <p className="plantillas__error" role="alert">
          {errorPropio ?? error}
        </p>
      )}

      <footer className="dialogo__acciones">
        <button type="button" className="boton" onClick={onCancelar}>
          Cancelar
        </button>
        <button
          type="button"
          className="boton boton--primario"
          disabled={noSePuedeGuardar}
          aria-busy={enCurso}
          onClick={intentarGuardar}
        >
          {editando ? "Guardar cambios" : "Guardar respuesta"}
        </button>
      </footer>
    </aside>
  )
}
